import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import styles from "./UserMenu.module.scss"
import { ReactComponent as CloseButton } from "../../../icons/close.svg"
import UserAvatar from "./UserAvatar"
import { isMobile } from "react-device-detect"
import {
  admin_dashboard_path,
  band_dashboard_path,
  discover_path,
  faq_path,
  new_event_path,
  new_post_path,
  own_bandnada_path,
  root_path,
  user_settings_path,
} from "../../../routes"
import { useMutation } from "react-query"
import { mutationRequest } from "../../../util/request"
import clsx from "clsx"
import { ShareBandNadaModal } from "../ShareBandNadaModal"
import { BandType } from "@models/bands"

interface UserMenuBand {
  name: string
  band_path: string
  band_type: BandType
  band_type_nice?: string
  edit_band_path: string
}

interface User {
  id: number
  name?: string
  avatar_url?: string
  avatar_url_small?: string
  avatar_url_medium?: string
  avatar_url_large?: string
  current_share_total?: number
  admin_type?: string
  band?: UserMenuBand
}

interface UserMenuProps {
  user?: User | null
}

const UserMenu: React.FC<UserMenuProps> = ({ user = null }) => {
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const mutation = useMutation(mutationRequest)

  user.avatar_url = user.avatar_url_small

  const handleLogout = async () => {
    try {
      await mutation.mutate({
        options: { method: "PUT" },
        path: `/api/v1/users/1/logout`,
      })
    } catch (e) {
      console.error("Failed to logout for some reason??", e)
    }
  }

  useEffect(() => {
    if (mutation.status === "success") {
      if (mutation.data.success) {
        localStorage.removeItem("jwt_token")
        window.location.href = root_path()
      } else {
        console.error("Logout mutation returned w/o success status")
      }
    } else if (mutation.status === "error") {
      console.error("Logout mutation returned with error status")
    }
  }, [mutation.status])

  return (
    <div>
      <UserAvatar
        handleClick={() => setShowUserMenu(!showUserMenu)}
        user={user}
        inHeader={true}
        size="small"
      />

      {showUserMenu && (
        <>
          <div className={`sm:hidden ${styles.overlay}`}></div>

          <ul
            onMouseLeave={() => setShowUserMenu(false)}
            className={styles.menu}
          >
            <div className="sm:hidden py-3 px-3 flex justify-end">
              <CloseButton
                onClick={() => setShowUserMenu(false)}
                className="ht-2"
              />
            </div>
            <li
              className={`font-semibold  ${
                user.current_share_total > 0 ? "" : "border-b-2 border-gray-200"
              } ${styles.item}`}
            >
              {user.band ? user.band.name : user.name}
            </li>
            {user.current_share_total > 0 ? (
              <li
                className={`font-semibold border-b-2 border-gray-200 ${styles.item}`}
              >
                {user.current_share_total} shares earned{" "}
                <Link
                  to={own_bandnada_path()}
                  className="text-blue-600 font-normal"
                >
                  (learn more)
                </Link>
              </li>
            ) : null}

            {isMobile && (
              <>
                <li className={styles.link}>
                  <Link className={styles.menuAnchor} to={discover_path()}>
                    Discover
                  </Link>
                </li>
                <li className={`border-b-2 border-gray-200 ${styles.link}`}>
                  <button
                    className={clsx(styles.menuAnchor, styles.menuButton)}
                    onClick={() => {
                      setShowUserMenu(false)
                      setShowShareModal(true)
                    }}
                  >
                    Share bandNada
                  </button>
                </li>
              </>
            )}
            {user.band && (
              <>
                <li className={styles.link}>
                  <Link className={styles.menuAnchor} to={user.band.band_path}>
                    View Your Page
                  </Link>
                </li>
                {user.band.band_type !== "fan" && (
                  <>
                    <li className={styles.link}>
                      <Link
                        className={styles.menuAnchor}
                        to={user.band.edit_band_path}
                      >
                        {user.band.band_type_nice} Settings
                      </Link>
                    </li>
                    <li className={styles.link}>
                      <Link
                        className={styles.menuAnchor}
                        to={band_dashboard_path()}
                      >
                        {user.band.band_type_nice} Dashboard
                      </Link>
                    </li>
                    <li className={styles.link}>
                      <Link className={styles.menuAnchor} to={new_event_path()}>
                        Create an Event
                      </Link>
                    </li>
                    <li className={`border-b-2 border-gray-200 ${styles.link}`}>
                      <Link className={styles.menuAnchor} to={new_post_path()}>
                        Create a Post
                      </Link>
                    </li>
                  </>
                )}
              </>
            )}
            {(user.admin_type === "full_admin" || user.admin_type === "editor") && (
              <li className={styles.link}>
                <a className={styles.menuAnchor} href={admin_dashboard_path()}>
                  Admin
                </a>
              </li>
            )}
            <li className={styles.link}>
              <Link className={styles.menuAnchor} to={user_settings_path()}>
                User Settings
              </Link>
            </li>
            <li className={styles.link}>
              <Link className={styles.menuAnchor} to={faq_path()}>
                Help / FAQ
              </Link>
            </li>
            <li className={styles.link}>
              <button
                className={clsx(styles.menuAnchor, styles.menuButton)}
                onClick={handleLogout}
                disabled={mutation.isLoading}
              >
                {mutation.isLoading || mutation.isSuccess
                  ? "Logging out..."
                  : "Logout"}
              </button>
            </li>
          </ul>
        </>
      )}

      <ShareBandNadaModal
        isOpen={showShareModal}
        onDismiss={() => setShowShareModal(false)}
      />
    </div>
  )
}

export default UserMenu
